// @ts-strict-ignore
import {
  Icon, IconName, TextField, useSnackBar,
} from '@usealloy/component-library';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const StyledIcon = styled(Icon)`
  position: absolute;
  bottom: 0;
  transform: translate(-30%, -50%);
  right: 0%;
`;

const FilterContainer = styled.div`
  min-width: 200px;
`;

export type RangeFilterProps = {
  facetName: string;
  subFacetName: string;
  filterLabel: string;
  lowerBound?: number;
  upperBound?: number;
  currentMin: number | undefined;
  currentMax: number | undefined;
  updateFilterBy: (filterName: string, { name, min, max }:
  { name: string; min: number; max: number }) => void;
  onClearFilter?: () => void;
};

const RangeFilter = ({
  facetName,
  subFacetName,
  filterLabel,
  lowerBound,
  upperBound,
  currentMin,
  currentMax,
  updateFilterBy,
  onClearFilter,
}: RangeFilterProps) => {
  const addSnackBar = useSnackBar();

  const [min, setMin] = useState<number | undefined>(currentMin);
  const [max, setMax] = useState<number | undefined>(currentMax);

  const clearFilter = () => {
    updateFilterBy(facetName, { name: subFacetName, max: null, min: null });
    if (onClearFilter) { onClearFilter(); }
  };

  const handleInvalidFilterValues = ({ id, title }) => {
    addSnackBar({
      duration: 3_500,
      id,
      title,
      type: 'warning',
    });
    setMin(currentMin);
    setMax(currentMax);
  };

  const getRangeStep = () => {
    if (!upperBound || !lowerBound) {
      return 1;
    }

    const boundDifference = Math.abs(upperBound - lowerBound);

    if (boundDifference > 50) {
      return 10;
    }

    if (boundDifference > 1) {
      return 1;
    }

    if (boundDifference > 0.1) {
      return 0.1;
    }

    return 0.001;
  };

  const applyFilter = () => {
    if ((min === null || min === undefined) && (max === null || max === undefined)) {
      clearFilter();
    }
    if (min > max) {
      handleInvalidFilterValues({
        id: 'validation-error-min-greater-than-max',
        title: `The ${filterLabel} filter is invalid. The minimum value must be less than or equal to the maximum value.`,
      });
    } else if (lowerBound && min < lowerBound) {
      handleInvalidFilterValues({
        id: 'validation-error-min-less-than-lower-bound',
        title: `The ${filterLabel} filter is invalid. The minimum value must be greater than or equal to ${lowerBound}.`,
      });
    } else if (upperBound && max > upperBound) {
      handleInvalidFilterValues({
        id: 'validation-error-max-greater-than-upper-bound',
        title: `The ${filterLabel} filter is invalid. The maximum value must be less than or equal to ${upperBound}.`,
      });
    } else {
      updateFilterBy(facetName, { name: subFacetName, max, min });
    }
  };

  useEffect(() => {
    const hasValue = val => val !== undefined && val !== null;

    // Wait for user to stop typing across entire form before applying filter
    if (hasValue(min) && hasValue(max)) {
      const timeOutId = setTimeout(() => applyFilter(), 500);
      return () => clearTimeout(timeOutId);
    }
    return () => {};
  }, [min, max]);

  return (
    <FilterContainer className="d-flex px-3 py-2 range-filter">
      <TextField
        min={lowerBound}
        max={max}
        value={min ?? ''}
        label="From"
        type="number"
        className="mr-3"
        step={getRangeStep()}
        onChange={event => {
          const { value } = event.target;
          if (value === '') {
            setMin(undefined);
          } else {
            setMin(+value);
          }
        }}
      />
      <TextField
        min={min}
        max={upperBound}
        value={max ?? ''}
        label="To"
        type="number"
        className="mr-3"
        step={getRangeStep()}
        onChange={event => {
          const { value } = event.target;
          if (value === '') {
            setMax(undefined);
          } else {
            setMax(+value);
          }
        }}
      />
      <StyledIcon icon={IconName.Xmark} width="20" height="20" onClick={() => clearFilter()} />
    </FilterContainer>
  );
};

export default RangeFilter;
